
import { defineComponent } from 'vue'
import { mapState } from 'vuex'
import AsahiSelectHeader from '@/components/library/AsahiSelectHeader.vue'
import AsahiProgressBar from '@/components/library/assets/AsahiProgressBar.vue'
import { permissionSchema } from '@/store/modules/users/interfaces/IUser'
import { TransportationTypes, getLocaleKeyForTransportationTypes } from '@/utils/transportTypes'
import { IAuthUser } from '@/store/modules/auth/interfaces/IAuthUser'
import { IShipToBySapId } from '@/store/modules/catalog/interfaces/shipto/IShipToResponse'
import DeliveryDatePicker from '@/components/cart/DeliveryDatePicker.vue'
import { getHolidayDays, isStandardDeliveryDate, validateDeliveryDate } from '@/utils/deliveryDate'
import shipToIcon from '@/components/library/assets/shipTo.svg'
import trackIcon from '@/components/library/assets/Truck.svg'
import calendarIcon from '@/components/library/assets/CalendarWhite.svg'
import { BaseCartQueryItem } from '@/store/modules/basket/interfaces/cartQuery/ICartQueryResponse'
import { format } from 'date-fns'
import WarningModal from '@/components/modals/WarningModal.vue'
import modals from '@/utils/modals'
import AsahiTypography from '@/components/library/Typography.vue'
import PlnModal from '@/components/modals/PlnModal.vue'
import AsahiDiscountMessage from '@/components/library/assets/AsahiDiscountMessage.vue'
import PaperViewDetail from '@/assets/icons/Paper-view-detail.vue'
import pageContent from '@/mixins/pageContent'
import LoaderSpinner from '@/components/common/LoaderSpinner.vue'
import { constants } from '@/constants'
import { CommonMutationType } from '@/store/modules/common/mutation-type'

interface RoleDetail {
rolename: string;
roleid: string;
permission: string;
roleType: string;
customerType: string;
possibleRoles: string;
}

const MORE_SECTION_META_VALUES = ['marketing', 'news']

const SHIP_TO_IDS_FOR_SMALL_TRUCK = [
  '1123069', '1128540', '1128541', '1132153', '1378446', '1451527', '1078585',
  '1106931', '1155500', '1147836', '1146023', '1153964', '1085003', '1084773',
  '1075622', '1093683', '1100456', '1579306', '1158332', '1539502', '1156832',
  '1604126', '1128540', '1128541', '1129556', '1129606', '1413065', '1080720',
  '1152086', '1152468', '1152760', '1273610', '1156069', '1149863', '1089260',
  '1104514', '1416172', '1266582', '1080721', '1320552', '1066949', '1148930',
  '1168627', '1113875', '1125857', '1375235', '1108869', '1438029', '1131934',
  '1132317', '1095627', '1112567', '1151988', '1521734', '1542465', '1115492',
  '1115496', '1115660', '1141697', '1142453', '1123069', '1063410', '1703422',
  '1063964', '1146401', '1073245', '1157566', '1064844', '1111689', '1155548',
  '1148854', '1148931', '1148984', '1117107', '1111029', '1143037', '1062195',
  '1144026', '1145460', '1146945', '1067872', '1266603', '1158660', '1152186',
  '1100463', '1132153', '1098807', '1150786', '1468275', '1542466', '1155717',
  '1102307', '1148668', '1090907', '1394830', '1170955', '1158810', '1151697',
  '1106753', '1090103', '1102368', '1127244', '1098841', '1129290', '1708572',
  '1266598', '1266585', '1163069', '1139246', '1085432', '1140959', '1155526',
  '1155525', '1266600', '1155499', '1605455', '1142682', '1084086', '1245819',
  '1173894', '1184254', '1184315', '1269775', '1063796', '1274183', '1275964',
  '1542467', '1335755', '1351099', '1353708', '1378446', '1548509', '1451527',
  '1465188', '1561033', '1469448', '1542464', '1535243', '1541037', '1543956',
  '1549221', '1554637', '1558094', '1561116', '1572062', '1573557', '1612569',
  '1576040', '1458396', '1600847', '1602373', '1602391', '1603558', '1604231',
  '1612578', '1616018', '1619699', '1701405', '1704801', '1707777', '1465230'
]

const STARTING_PAGE_SIZE = 24

const CATALOG_PAGE_SIZE = 1000

export default defineComponent({
  components: { DeliveryDatePicker, AsahiSelectHeader, AsahiProgressBar, AsahiTypography, AsahiDiscountMessage, LoaderSpinner },
  props: {
    changeMessageColor: {
      type: Boolean,
      default: false
    },
    cartId: {
      type: String,
      default: ''
    },
    user: {
      type: Object,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['shipToChange'],
  data () {
    return {
      shipToIcon,
      trackIcon,
      calendarIcon,
      selectedShipmentId: '',
      errorMsg: '',
      loading: false,
      successWeight: false,
      errorWeight: false,
      shipmentSet: false,
      selectedOrderType: '',
      deliveryDateSelected: '',
      shipTo: {},
      usedShipTo: '',
      usedTransportType: '',
      usedDeliveryDate: '',
      billingAddressForm: {} as any,
      options: [] as any,
      deliveryDatePickerHeight: 0,
      isExpanded: false,
      shipToLoad: 0,
      transportLoad: 0,
      isDatePickerOpen: false,
      userChangedFlag: {
        shipTo: false,
        transportType: false
      },
      bufferedShipId: '',
      bufferedPageSize: STARTING_PAGE_SIZE
    }
  },
  computed: {
    ...mapState('basket', ['id', 'query', 'shipmentId', 'deliveryDate', 'items',
      'paymentMethod', 'availableDate', 'availableDays']),
    ...mapState('catalog', ['alignment', 'shipTos', 'shipToAddress', 'shipToMethod', 'paymentMethod', 'productFreeBeer']),
    ...mapState('main', ['holidaysConfig']),
    ...mapState('auth', ['user']),

    isQuickOrderPage () {
      return this.$route.path === '/catalog/quickorder'
    },
    customerType () {
      const customerTypeArray = this.$store.state.auth.user?.permissionSchema?.filter(permission => permission?.rpDetails?.some((item: RoleDetail) => item?.customerType?.toLowerCase() !== ''))
      return customerTypeArray ? customerTypeArray[0]?.rpDetails[0]?.customerType : ''
    },
    isOnTradeCustomer () {
      return this.customerType ? (this.customerType.toLowerCase() === 'ontrade') : false
    },
    isDistributorCustomer () {
      return this.customerType ? (this.customerType.toLowerCase() === 'distributor') : false
    },

    isFreeBeerCheckActive () {
      return this.query?.free_beer_active
    },
    allProductAdded () {
      return !(this.productFreeBeer.some((item: any) => item.item_id === '0'))
    },
    productAvailable () {
      const productData = this.productFreeBeer.filter((item: any) => item.item_id === '0')
      return productData ? productData.length : 0
    },
    productAdded () {
      const productData = this.productFreeBeer.filter((item: any) => item.item_id !== '0')
      return productData ? productData.length : 0
    },
    isAllProductAdded () {
      if (this.productFreeBeer && this.productAdded > 0 && this.productFreeBeer.length === this.productAdded) {
        return true
      }
      return false
    },
    isMoreSection (): boolean {
      return MORE_SECTION_META_VALUES.includes(this.$route?.meta?.tabSelection as string)
    },
    queries () {
      return this.$store.state.catalog.queries
    },

    defaultPaymentMethod () {
      return this.paymentMethod?.defaultmethod?.[0] || null
    },

    formattedDeliveryDate () {
      if (!this.deliveryDate) return ''
      const args = this.deliveryDate.split('-')
      return args[2] + '.' + args[1]
    },
    currentShippingPlace () {
      return this.shipToOptions.find((s: any) => s.value === this.selectedShipmentId)
    },
    currentShippingMethod () {
      return this.transportOptionsAsSelectBoxOptions.find((t: any) => t.value === this.shipmentId)
    },

    shipToOptions () {
      const { permissionSchema = [] } = this.user || {}
      return permissionSchema
        .filter((permission: any) =>
          permission.rpDetails.some((role: any) => role.permission.includes('Catalogue'))
        )
        .map((permission: any) => ({
          label: permission.shipToName,
          value: permission.shipToId,
          description: this.shipToAddressCity(permission.shipToId)
        }))
    },

    progressBar () {
      return this.query?.progress_bar_data
    },
    maxOrderCash () {
      return this.progressBar?.iMaximumOrderCash ?? 0
    },
    grandTotalYourPrice () {
      return this.query?.grand_total_your_price_details?.grand_total_your_price ?? 0
    },
    cashFilledPercentage () {
      return (this.grandTotalYourPrice / this.maxOrderCash) * 100
    },
    messageColorClass () {
      let msgColor = ''
      let borderColor = ''
      if (this.changeMessageColor) {
        if (this.errorWeight === true) {
          msgColor = 'error-text'
          borderColor = 'border-error-text'
        } else if (this.successWeight === true) {
          msgColor = 'success-text'
          borderColor = 'border-success-text'
        } else {
          msgColor = 'default-text'
          borderColor = 'border-default-text'
        }
      }
      return { msgColor, borderColor }
    },
    transportOptions () {
      let transportList = []
      if (this.shipToMethod) {
        transportList = this.shipToMethod.map((item: any) => ({
          label: item.title,
          value: item.value,
          checked: this.selectedOrderType === item.value,
          defaultShippingMethod: item.defaultShippingMethod
        }))
      }
      return transportList
    },
    transportOptionsAsSelectBoxOptions () {
      const transportList = this.transportOptions.map((option: any) => ({
        label: this.$t(option.label),
        value: option.value,
        default: option.defaultShippingMethod
      }))
      return this.isQuickOrderPage ? transportList.filter((item: any) => item.value === 'flatrate_flatrate') : transportList
    },
    paymentCode () {
      let paymentMethod = { sap_payment_code: '', sap_payment_method: '' }
      if (this.query?.sap_payment_details) {
        paymentMethod = JSON.parse(this.query?.sap_payment_details)
      }
      return paymentMethod
    },
    cartItems () {
      let cartItems: Array<{ productid: string }> = []
      if (this.items.length) {
        cartItems = this.items.map((item: BaseCartQueryItem) => {
          return { productId: item.product.sku }
        })
      }
      return cartItems
    },
    defaultShipTo () {
      return this.$store.state.auth.defaultShipTo
    },
    freeBeersRunning () {
      return this.$store.state.catalog.freeBeersRunning
    }
  },
  methods: {
    shipToAddressCity (shipToId: string) {
      const entry = this.shipToAddress?.find((item: any) => item.pe_idsap === shipToId)

      if (entry) {
        const addressParts = []

        // Check each field and push to addressParts if valid
        if (entry?.address1_line1) {
          addressParts.push(entry.address1_line1)
        }
        if (entry?.pe_address1_housenumber) {
          addressParts.push(entry.pe_address1_housenumber)
        }
        if (entry?.address1_city) {
          addressParts.push(entry.address1_city)
        }

        // Join the valid parts with a comma and return
        return addressParts.join(', ')
      }
      return ''
    },
    async bufferedFetchPaymentMethod () {
      if (this.bufferedShipId === '') {
        this.bufferedShipId = (this.query?.ship_to_id || this.selectedShipmentId)
        return this.$store.dispatch(
          'catalog/FETCH_PAYMENT_METHOD',
          { ship_to_id: (this.query?.ship_to_id || this.selectedShipmentId) },
          { root: true }
        )
      } else if (this.bufferedShipId !== this.selectedShipmentId) {
        this.bufferedShipId = this.selectedShipmentId
        return this.$store.dispatch(
          'catalog/FETCH_PAYMENT_METHOD',
          { ship_to_id: (this.selectedShipmentId) },
          { root: true }
        )
      } else {
        return {}
      }
    },
    handleDatePickerOpen () {
      this.isDatePickerOpen = true
    },
    handleDatePickerClose () {
      this.isDatePickerOpen = false
    },
    showPlnModal () {
      modals.showModal({
        component: PlnModal,
        isTutorialModal: false,
        props: {
          isOnTradeCustomer: this.isOnTradeCustomer
        },
        listeners: {}
      })
    },
    showWarningModal (message: string,
      secondaryMessage: string,
      primaryButtonText: string,
      secondaryButtonText: string,
      messageAdditional: string,
      type: string,
      newValue: string,
      oldValue: string) {
      modals.showModal({
        component: WarningModal,
        props: {
          selectionType: type,
          messageHeading: message,
          messageSubHeading: secondaryMessage,
          primaryButtonText,
          messageAdditional,
          secondaryButtonText,
          newValue,
          oldValue
        },
        listeners: {
          warningAction: (action: string) => {
            if (action === 'yes' && type === 'shipTo') {
              this.setShipment(newValue)
            }
            if (action === 'yes' && type !== 'shipTo') {
              this.onOrderTransportTypeSelection(newValue)
            }
          }
        }
      })
    },
    toggleAccordion (event: any, index: number) {
      if (event.target.getAttribute('aria-expanded') === 'true') {
        this.isExpanded = true
      } else {
        this.isExpanded = false
      }
    },

    async changeShipTo (value: string) {
      const message = this.$t('By changing the ship to, the products in you cart will be canceled and all the changes will be lost.')
      const secondaryMessage = this.$t('Are you sure you want to proceed?')
      const primaryButtonText = this.$t('Yes, proceed')
      const secondaryButtonText = this.$t('No, maintain ship to')
      const messageAdditional = ''
      this.userChangedFlag.shipTo = true
      this.userChangedFlag.transportType = true

      if (this.items?.length > 0) {
        this.showWarningModal(
          message,
          secondaryMessage,
          primaryButtonText,
          secondaryButtonText,
          messageAdditional,
          'shipTo',
          value,
          this.selectedShipmentId)
      } else {
        await this.setShipment(value)
      }
    },
    changeDeliveryType (value: string) {
      const message = this.$t('By changing the type of transport, the minimum capacity will change.')
      const secondaryMessage = this.$t('Are you sure you want to proceed?')
      const primaryButtonText = this.$t('Yes, proceed')
      const secondaryButtonText = this.$t('Back')
      const messageAdditional = value === 'freeshipping_freeshipping' ? this.$t('You order a small car. You lose the logistics discount!') : ''

      this.showWarningModal(
        message,
        secondaryMessage,
        primaryButtonText,
        secondaryButtonText,
        messageAdditional,
        'shipMethod',
        value,
        this.selectedOrderType)
    },

    async setShipment (value: string) {
      this.errorMsg = ''
      this.bufferedPageSize = STARTING_PAGE_SIZE
      if (this.selectedShipmentId !== value) {
        this.$store.commit(CommonMutationType.INCREMENT_LOADING)
        this.selectedShipmentId = value
        this.errorMsg = ''
        if (this.checkDisableShipTo(this.user, this.selectedShipmentId)) {
          this.errorMsg = this.$t('ShipTo suspended - Select another ShipTo')
          return
        }

        try {
          this.loading = true
          await this.bufferedFetchPaymentMethod()
          await this.$store.dispatch(
            'catalog/FETCH_SHIP_TO_METHOD',
            { ship_to_id: this.selectedShipmentId },
            { root: true }
          )

          if (this.shipToMethod) {
            this.shipmentSet = true
            this.setDefaultTransportType()
            this.onOrderTransportTypeSelection(this.selectedOrderType)
          }

          this.$emit('shipToChange', this.selectedShipmentId)
        } catch (error) {
          console.error('shipment error', error)
          this.errorMsg = 'Error setting shipment'
        } finally {
          await this.$store.commit('basket/SET_LOADING', false, { root: true })
        }
        this.loading = false
        const companyId = this.getCompanyid(this.selectedShipmentId, this.user)
        const event = new CustomEvent('setShipment', {
          detail: {
            type: 'delivery-options',
            shipTo: this.selectedShipmentId,
            payer: companyId
          }
        })
        document.dispatchEvent(event)
        this.$store.commit(CommonMutationType.DECREMENT_LOADING)
      }
    },
    setDefaultTransportType () {
      if (!this.userChangedFlag.transportType && this.query?.delivery_method != null && this.query?.delivery_method !== '' && this.query?.delivery_method !== undefined) {
        if (!localStorage.getItem('shippingMethod')) {
          this.selectedOrderType = this.query?.delivery_method
        } else {
          this.selectedOrderType = localStorage.getItem('shippingMethod') || ''
        }
      } else {
        if (localStorage.getItem('shippingMethod') && localStorage.getItem('shippingMethodSetted')) {
          this.selectedOrderType = this.transportOptionsAsSelectBoxOptions.find((e: any) => e.value === localStorage.getItem('shippingMethod')).value
        } else {
          this.selectedOrderType = this.transportOptionsAsSelectBoxOptions.find((e: any) => e.default === true).value
        }

        this.userChangedFlag.transportType = false
      }
      if (this.userChangedFlag.shipTo && this.query?.delivery_method != null && this.query?.delivery_method !== '' && this.query?.delivery_method !== undefined) {
        this.selectedOrderType = this.transportOptionsAsSelectBoxOptions.find((e: any) => e.default === true).value
      }
    },
    onResizeDeliveryDatePicker ({ height }: {
    height: number
  }) {
      this.deliveryDatePickerHeight = height
    },
    getCompanyid (shipToId: string, user: IAuthUser) {
      let companyId = ''
      const shipToItem = user?.permissionSchema.find((item: permissionSchema) => item.shipToId === shipToId)
      if (shipToItem) {
        companyId = shipToItem?.companyId
      }
      return companyId
    },
    checkDisableShipTo (user: IAuthUser, shipToId: string) {
      const permissionSchema = user?.permissionSchema || []

      if (shipToId != null) {
        return permissionSchema.some(
          (shipToItem: permissionSchema) => shipToItem.shipToId === shipToId && shipToItem.shipToStatus === 'SUSPENDED'
        )
      }

      if (permissionSchema.length === 1) {
        return permissionSchema[0]?.shipToStatus === 'SUSPENDED'
      }

      return false
    },
    getPaymentData () {
      const payment = { code: '', method: '' }
      if (this.paymentCode?.sap_payment_code !== '') {
        payment.code = this.paymentCode?.sap_payment_code
        payment.method = this.paymentCode?.sap_payment_method
      } else {
        payment.code = this.defaultPaymentMethod?.value
        payment.method = this.defaultPaymentMethod?.title
      }
      return payment
    },
    async fetchFreeBeer () {
      if (this.id) {
        this.$store.commit(CommonMutationType.INCREMENT_LOADING)
        await this.$store.dispatch(
          'catalog/FETCH_FREEBEER_PRODUCT',
          {
            cartId: this.id,
            rand: Math.random()
          },
          { root: true }
        )

        await this.$store.dispatch('basket/FETCH_CART_QUERY', { cart_id: this.id }, { root: true })
        this.$store.commit(CommonMutationType.DECREMENT_LOADING)
      }
    },
    async startYourOrder () {
      const errorMsg = []
      if (this.selectedOrderType === '') errorMsg.push(this.$t('Order type has to not be empty!'))
      if (this.deliveryDateSelected === '') errorMsg.push(this.$t('Delivery date has to not be empty!'))
      if (errorMsg.length > 0) {
      // this.errorMsg = errorMsg[0]
        return
      }
      if (!validateDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos, getHolidayDays(this.holidaysConfig))) {
        (this.$refs.deliveryDatePickerRef as InstanceType<typeof DeliveryDatePicker>)?.forceRecalculate()
        errorMsg.push(this.$t('Shipping cannot be ordered. Choose a different date.'))
      }
      if (errorMsg.length > 0) {
        this.errorMsg = errorMsg[0]
        return
      }

      // get new orderable units based on new order type
      localStorage.setItem('deliveryDate', this.deliveryDateSelected)
      localStorage.setItem(
        'standardDeliveryDate',
        Number(isStandardDeliveryDate(this.deliveryDateSelected, this.selectedOrderType, this.shipTos)).toString()
      )
      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })

      const getPaymentMethod = this.getPaymentData()
      const deliveryDate = localStorage.getItem('deliveryDate')

      if (this.checkChanges()) {
        this.usedShipTo = localStorage.getItem('shipToId') || ''
        this.usedTransportType = this.selectedOrderType
        this.usedDeliveryDate = localStorage.getItem('deliveryDate') as string
        await this.$store.dispatch(
          'basket/FETCH_TRANSPORT_TYPE',
          {
            transporttype: this.selectedOrderType,
            cart_id: this.id,
            sappaymentmethod: localStorage.getItem('paymentMethodName'),
            sappaymentcode: localStorage.getItem('paymentMethod'),
            outletid: Number(localStorage.getItem('shipToId')),
            datepopup: localStorage.getItem('deliveryDate') as string
          },
          { root: true }
        )
      }

      if (!this.isOnTradeCustomer && this.$route.name !== 'quickorder' && !this.freeBeersRunning) {
        await this.fetchFreeBeer()
      }

      try {
        this.$store.commit(CommonMutationType.INCREMENT_LOADING)
        await Promise.all([
          this.fetchProducts(),
          this.fetchWishlist(),
          this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.id }, { root: true })
        ])
        this.$store.commit(CommonMutationType.DECREMENT_LOADING)
      } catch (error) {
        console.error('order modal error', error)
        this.$store.commit(CommonMutationType.DECREMENT_LOADING)
      }
    },
    checkChanges () {
      return (this.usedShipTo !== localStorage.getItem('shipToId')) || (this.usedTransportType !== this.selectedOrderType) || (this.usedDeliveryDate !== localStorage.getItem('deliveryDate') as string)
    },
    async fetchProducts () {
      if (this.$route.name === 'homepage') {
        this.fetchLowStockProduct()
      }
      if (this.$route.name === 'favorites' || this.$route.name === 'products') {
        this.bufferedFetchProduct()
      } if (this.$route.name === 'specialoffer') {
        await this.$store.dispatch('catalog/FETCH_SPECIAL_OFFERS', { ...this.queries, category_id: [constants.categories.specialOffer.toLocaleString()], fetchPolicy: 'network-only' }, { root: true })
      } if (this.$route.name === 'quickorder') {
        await this.$store.dispatch('catalog/FETCH_QUICK_ORDERS', { ...this.queries, category_id: [constants.categories.quickOrder.toLocaleString()], fetchPolicy: 'network-only' }, { root: true })
      }
    },

    async bufferedFetchProduct () {
      await this.$store.dispatch('catalog/FETCH_PRODUCTS', { ...this.queries, pageSize: this.bufferedPageSize, bypassLoading: this.bufferedPageSize !== STARTING_PAGE_SIZE, fetchPolicy: 'network-only' }, { root: true })
      if (this.bufferedPageSize === STARTING_PAGE_SIZE) {
        this.bufferedPageSize = CATALOG_PAGE_SIZE
        this.bufferedFetchProduct()
      }
    },

    fetchWishlist () {
      this.$store.commit(CommonMutationType.INCREMENT_LOADING)

      this.$store.dispatch('catalog/FETCH_WISHLIST', { root: true })
      this.$store.commit(CommonMutationType.DECREMENT_LOADING)

      if (this.$route.name === 'favorites') {
        this.$store.dispatch('catalog/FETCH_FAVORITES', {}, { root: true })
      }
    },
    async fetchLowStockProduct () {
      this.$store.commit(CommonMutationType.INCREMENT_LOADING)
      const
        lowStockQueries = this.isOnTradeCustomer
          ? {
              sort: { brand: 'ASC' },
              pageSize: 50,
              is_peroni: false,
              currentPage: 1,
              material_type: 'GLASSWARE'
            }
          : {
              sort: { brand: 'ASC' },
              pageSize: 10,
              is_peroni: false,
              currentPage: 1,
              product_lowstock: 1
            }
      this.$store.commit('catalog/SET_SHOW_LOW_STOCK_PRODUCT', false, { root: true })
      this.$store.dispatch('catalog/FETCH_LOW_STOCK_PRODUCTS', lowStockQueries, { root: true })
      this.$store.commit(CommonMutationType.DECREMENT_LOADING)
    },

    async initDefaultShipmentId () {
      const selectedShipTo = localStorage.getItem('shipToId') ?? ''
      if (this.shipToOptions.length > 0 && selectedShipTo === '') {
        this.setShipment(this.shipToOptions[0].value)
      } else {
        this.setShipment(selectedShipTo)
      }
      this.setShipment(this.selectedShipmentId)
      if (this.holidaysConfig == null || this.holidaysConfig?.holidays?.length === 0) await this.$store.dispatch('main/FETCH_HOLIDAYS_CONFIG', { root: true })
    },
    async onOrderTransportTypeSelection (value: string) {
      this.bufferedPageSize = STARTING_PAGE_SIZE
      const alreadySetDeliveryDate = localStorage.getItem('deliveryDate') ?? ''
      this.selectedOrderType = value
      this.loading = true
      await this.$store.commit('basket/SET_SHIPMENT_ID', this.selectedOrderType, { root: true })
      const storeId = localStorage.getItem('store')
      if (this.selectedShipmentId != null && storeId != null) {
        await this.$store.dispatch('basket/GET_FIRST_AVAILABLE_DATE', {
          ShipToId: this.selectedShipmentId,
          store: storeId
        })
        // Check is already selected delivery date exist in available days array
        if (this.userChangedFlag.shipTo) {
          const firstAvailableDate = this.availableDate ? this.availableDate : new Date().toISOString()
          this.deliveryDateSelected = firstAvailableDate.slice(0, 10)
          localStorage.setItem('deliveryDate', this.deliveryDateSelected)
          this.userChangedFlag.shipTo = false
        } else {
          const doesExistPreSelectedDate = this.availableDays.filter((d: string) => alreadySetDeliveryDate !== '' && d.includes(alreadySetDeliveryDate))
          if (doesExistPreSelectedDate.length > 0) {
            this.deliveryDateSelected = alreadySetDeliveryDate
          } else {
            const firstAvailableDate = this.availableDate ? this.availableDate : new Date().toISOString()
            this.deliveryDateSelected = firstAvailableDate.slice(0, 10)
          }
        }
        this.loading = false
        this.startYourOrder()
      }
      this.loading = false
    },
    onDateSelection (date: string) {
      this.bufferedPageSize = STARTING_PAGE_SIZE
      this.deliveryDateSelected = date
      this.$store.commit('basket/SET_DELIVERY_DATE', this.deliveryDateSelected, { root: true })
      this.startYourOrder()
    }
  },
  async mounted () {
    this.initDefaultShipmentId()
    this.$store.commit('basket/SET_SHIPMENT_ID', this.selectedOrderType, { root: true })
    await this.bufferedFetchPaymentMethod()
  },
  watch: {
    isQuickOrderPage (newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          this.onOrderTransportTypeSelection('flatrate_flatrate')
        }
      }
    },
    selectedShipmentId (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setShipment(newValue)
      }
    },
    availableDate (newDate, oldDate) {
      if (newDate !== oldDate) {
        this.deliveryDate = newDate
      }
    },
    defaultShipTo (defaultShipTo, oldDefaultShipTo) {
      if (defaultShipTo && defaultShipTo !== oldDefaultShipTo) {
        this.initDefaultShipmentId()
        this.userChangedFlag.shipTo = true
        this.fetchFreeBeer()
      }
    }
  }
})
